import type { Grid } from "../types.ts";
import type { FoldLineHover } from "./hit-detection.ts";
import { getTopColor } from "../engine/grid.ts";
import {
  BACKGROUND_COLOR,
  CELL_BORDER_RADIUS,
  EMPTY_CELL_COLOR,
  FOLD_LINE_COLOR,
  FOLD_LINE_HOVER_COLOR,
  FOLD_LINE_SELECTED_COLOR,
  getCellDisplayColor,
  GRID_LINE_COLOR,
} from "./colors.ts";

export interface BoardRenderState {
  hoveredFoldLine: FoldLineHover | null;
  selectedFoldLine: FoldLineHover | null;
}

export function computeLayout(
  grid: Grid,
  width: number,
  height: number,
  padding: number = 32
) {
  const maxCellW = (width - padding * 2) / grid.cols;
  const maxCellH = (height - padding * 2) / grid.rows;
  const cellSize = Math.floor(Math.min(maxCellW, maxCellH, 120));
  const gridW = grid.cols * cellSize;
  const gridH = grid.rows * cellSize;
  const offsetX = Math.floor((width - gridW) / 2);
  const offsetY = Math.floor((height - gridH) / 2);
  return { cellSize, offsetX, offsetY, gridW, gridH };
}

export function drawBoard(
  ctx: CanvasRenderingContext2D,
  grid: Grid,
  width: number,
  height: number,
  state: BoardRenderState
) {
  const { cellSize, offsetX, offsetY, gridW, gridH } = computeLayout(
    grid,
    width,
    height
  );

  // Background
  ctx.fillStyle = BACKGROUND_COLOR;
  ctx.fillRect(0, 0, width, height);

  const gap = 3;

  // Draw cells
  for (let r = 0; r < grid.rows; r++) {
    for (let c = 0; c < grid.cols; c++) {
      const stack = grid.cells[r]![c]!;
      const topColor = getTopColor(stack);

      const x = offsetX + c * cellSize + gap;
      const y = offsetY + r * cellSize + gap;
      const w = cellSize - gap * 2;
      const h = cellSize - gap * 2;

      ctx.fillStyle = getCellDisplayColor(topColor);
      roundRect(ctx, x, y, w, h, CELL_BORDER_RADIUS);
      ctx.fill();

      // Stack depth indicator
      if (stack.length > 1) {
        ctx.fillStyle = "rgba(255,255,255,0.5)";
        ctx.font = `${Math.max(10, cellSize * 0.18)}px monospace`;
        ctx.textAlign = "right";
        ctx.textBaseline = "bottom";
        ctx.fillText(`${stack.length}`, x + w - 2, y + h - 2);
      }
    }
  }

  // Draw fold lines (dashed, between cells)
  drawFoldLines(ctx, grid, offsetX, offsetY, cellSize, gridW, gridH, state);
}

function drawFoldLines(
  ctx: CanvasRenderingContext2D,
  grid: Grid,
  offsetX: number,
  offsetY: number,
  cellSize: number,
  gridW: number,
  gridH: number,
  state: BoardRenderState
) {
  ctx.lineWidth = 2;

  // Horizontal fold lines
  for (let line = 1; line < grid.rows; line++) {
    const y = offsetY + line * cellSize;
    const isHovered =
      state.hoveredFoldLine?.axis === "horizontal" &&
      state.hoveredFoldLine.line === line;
    const isSelected =
      state.selectedFoldLine?.axis === "horizontal" &&
      state.selectedFoldLine.line === line;

    ctx.strokeStyle = isSelected
      ? FOLD_LINE_SELECTED_COLOR
      : isHovered
        ? FOLD_LINE_HOVER_COLOR
        : FOLD_LINE_COLOR;
    ctx.setLineDash(isHovered || isSelected ? [] : [6, 4]);
    ctx.beginPath();
    ctx.moveTo(offsetX, y);
    ctx.lineTo(offsetX + gridW, y);
    ctx.stroke();
  }

  // Vertical fold lines
  for (let line = 1; line < grid.cols; line++) {
    const x = offsetX + line * cellSize;
    const isHovered =
      state.hoveredFoldLine?.axis === "vertical" &&
      state.hoveredFoldLine.line === line;
    const isSelected =
      state.selectedFoldLine?.axis === "vertical" &&
      state.selectedFoldLine.line === line;

    ctx.strokeStyle = isSelected
      ? FOLD_LINE_SELECTED_COLOR
      : isHovered
        ? FOLD_LINE_HOVER_COLOR
        : FOLD_LINE_COLOR;
    ctx.setLineDash(isHovered || isSelected ? [] : [6, 4]);
    ctx.beginPath();
    ctx.moveTo(x, offsetY);
    ctx.lineTo(x, offsetY + gridH);
    ctx.stroke();
  }

  ctx.setLineDash([]);
}

function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number
) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r);
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
}
